import { useAppDispatch, useAppSelector } from '../../../../contexts/hooks';
import { newToast, NoDataPanel } from '../../..';
import { shallowEqual } from 'react-redux';
import { formatMessage } from 'devextreme/localization';
import { Button, DataGrid, Toolbar, Tooltip, Popup, FileUploader } from 'devextreme-react';
import { useEffect, useState, useRef } from 'react';
import { Column } from 'devextreme-react/data-grid';
import { Item } from 'devextreme-react/toolbar';
import { AddIcon, PdfIcon, UnPdfIcon, RemoveIcon, Pin, UnPin } from '../../../../images/icons';
import { ToolbarItem } from 'devextreme-react/popup';
import { SelectBox } from 'devextreme-react/select-box';
import { ValueChangedEvent } from 'devextreme/ui/select_box';
import { useApiStorage } from '../../../../api/api.Storage';
import moment from 'moment';
import { useScreenSize } from '../../../../utils/media-query';
import ConfirmationPopup from '../../../utils/popup-confirmation/PopupConfirmation';
import { setPanelPined, setPanelVisible } from '../../../../contexts/slices/orders';

const Index = () => {
  const idOrd = useAppSelector((x) => x.globalOrders.CurrentOrder.IdOrder);
  const studies = useAppSelector((x) => x.globalOrders.CurrentOrder.StudiesData);
  
  const Prm_Ord_Attachments_C = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_Attachments_C, shallowEqual) ?? false;
  const Prm_Ord_Attachments_D = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_Attachments_D, shallowEqual) ?? false;
  const currentAttachments = useAppSelector((x) => x.globalOrders.CurrentOrder.Attachments);
  const [data, setData] = useState<any>([]);
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [showPdfPopup, setShowPdfPopup] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const uploadedFile = useRef<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [availableStudies, setAvailableStudies] = useState<any[]>([]);
  const [selectedStudy, setSelectedStudy] = useState<number | null>(null);
  const [validateAsAttachment, setValidateAsAttachment] = useState(false);
  const [modalConfirmation, setModalConfirmation] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<any>(null);
  const { Upload, Delete, GetFile } = useApiStorage();
  const { isXSmall, isSmall } = useScreenSize();
  const panelVisible = useAppSelector((x) => x.globalOrders.PanelVisible, shallowEqual);
  const dispatch = useAppDispatch();
  const handlePanelClose = () => {
    dispatch(setPanelVisible(true));
    dispatch(setPanelPined(false));
  };

  useEffect(() => {
    if (studies && currentAttachments) {
      const data = studies.map((study) => {
        const hasAttachment = currentAttachments.some((attachment) => attachment.Oad_Es_Id === study.Es_Id);
        const currentAttachment = currentAttachments.find((attachment) => attachment.Oad_Es_Id === study.Es_Id);
        return {
          Id: study.Es_Id,
          Estudio: study.Es_Nombre,
          Codigo: study.Es_Codigo,
          Archivo: hasAttachment,
          Oad_Id: currentAttachment ? currentAttachment.Oad_Id : null,
        };
      });
      setData(data);
    }
  }, [studies, currentAttachments]);

  useEffect(() => {
    if (studies && currentAttachments) {      
      const attachedStudyIds = currentAttachments.filter((attachment) => attachment.Oad_Es_Id !== null).map((attachment) => attachment.Oad_Es_Id);
      const filteredStudies = studies.filter((study) => !attachedStudyIds.includes(study.Es_Id));
      setAvailableStudies(filteredStudies);
    }
  }, [studies, currentAttachments]);

  const handleFileUpload = (e) => {
    const file = e.value[0];
    if (file && file.type === 'application/pdf') {
      uploadedFile.current = file;
    } else {
      uploadedFile.current = null;
      alert('Solo se permiten archivos con extensión .pdf');
    }
  };

  const handleAccept = async () => {
    if (!Prm_Ord_Attachments_C) {
      newToast('WARNING', 'No tiene permisos para crear archivos adjuntos.', 4);
      return;
    }

    if (!uploadedFile.current) {
      newToast('WARNING', 'Debe adjuntar un archivo antes de continuar.', 4);
      return;
    }

    if (!idOrd || idOrd === -1) {
      newToast('WARNING', 'El ID de la orden es obligatorio.', 4);
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadedFile.current);
    formData.append('idOrd', idOrd.toString());
    if (selectedStudy) {
      formData.append('idEst', selectedStudy.toString());
    }
    formData.append('validate', validateAsAttachment.toString());

    try {
      setLoading(true);
      await Upload(formData);
      // Si hay un estudio seleccionado, actualizar availableStudies removiendo el estudio cargado
      if (selectedStudy) {
        setAvailableStudies((prev) => prev.filter((study) => study.Es_Id !== selectedStudy));
      }
      handleCancel();
    } catch (error) {
      console.error('Error al subir el archivo:', error);
      alert('Hubo un problema al subir el archivo. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    uploadedFile.current = null;
    setSelectedStudy(null);
    setValidateAsAttachment(false);
    setPopupVisible(false);
  };

  const handleStudyChange = (e: ValueChangedEvent) => {
    const value = e.value as number | null;
    setSelectedStudy(value);
    setValidateAsAttachment(false);
  };

  const handleDeleteAttachment = async (data: any) => {
    if (!Prm_Ord_Attachments_D) {
      newToast('WARNING', 'No tiene permisos para eliminar archivos adjuntos.', 4);
      return;
    }
    if (data) {
      setSelectedAttachment(data);
      setModalConfirmation(true);
    }
  };

  const handleClicOpen = async (data: any) => {
    if (data) {
      setShowPdfPopup(true); // Open the modal immediately
      setPdfBlobUrl(null); // Clear any previous PDF

      try {
        const base64 = await GetFile(data.Oad_Id);        
        if (base64) {
          const sanitizedBase64 = base64.file.replace(/\s/g, ''); // Clean up spaces for safety
          const fullDataUrl = `data:application/pdf;base64,${sanitizedBase64}`;

          setPdfBlobUrl(fullDataUrl); // Set the PDF URL once fetched
        } else {
          alert('No se pudo obtener el archivo. Por favor, intente nuevamente.');
        }
      } catch (error) {
        console.error('Error al procesar el archivo PDF:', error);
        alert('El archivo recibido no es válido.');
      }
    }
  };
  
  const renderAttachedFiles = () => {
    if (!currentAttachments || !Array.isArray(currentAttachments)) {
      return null;
    }

    return (
      <div className='custom-card-container'>
        {currentAttachments
          .filter((attachment) => attachment.Oad_Es_Id === null)
          .map((attachment) => {
            const nameParts = attachment.Oad_Nombre.split('-');
            const displayName = nameParts.length > 2 ? nameParts.slice(2).join('-') : attachment.Oad_Nombre;
            const truncatedName = displayName.length > 15 ? `${displayName.substring(0, 15)}...` : displayName;

            return (
              <div key={attachment.Oad_Id}>
                <div id={`file${attachment.Oad_Id}`} className='custom-card'>
                  {!isXSmall && !isSmall && Prm_Ord_Attachments_D && <img src={RemoveIcon} alt='Remove Icon' className='delete-icon' onClick={() => handleDeleteAttachment(attachment)} title='Eliminar' />}
                  <div
                    className='custom-card-body'
                    onClick={() => {
                      handleClicOpen(attachment);
                    }}>
                    <img src={PdfIcon} alt='PDF Icon' className='custom-card-icon' />
                    <p className='file-size'>{(attachment.Oad_Tamanno / 1024).toFixed(2)} KB</p>
                    <div className='custom-card-header' title={displayName} data-tooltip={displayName}>
                      {truncatedName}
                    </div>
                  </div>
                  {(isXSmall || isSmall) && Prm_Ord_Attachments_D && (
                    <button className='delete-button' onClick={() => handleDeleteAttachment(attachment)}>
                      Eliminar
                    </button>
                  )}
                </div>
                <Tooltip target={`#file${attachment.Oad_Id}`} showEvent='mouseenter' hideEvent='mouseleave' position={'bottom'}>
                  <div className='tooltip-content'>
                    <h6 className='tooltip-title'>Detalles del archivo</h6>
                    <p className='tooltip-item'>
                      <strong>Nombre: </strong>
                      {displayName}
                    </p>
                    <p className='tooltip-item'>
                      <strong>Tamaño: </strong>
                      {(attachment.Oad_Tamanno / 1024).toFixed(2)} KB
                    </p>
                    <p className='tooltip-item'>
                      <strong>Fecha de carga: </strong>
                      {moment(attachment.Oad_Fecha).format('DD/MM/YYYY HH:mm')}
                    </p>
                  </div>
                </Tooltip>
              </div>
            );
          })}
      </div>
    );
  };

  const handleConfirmDelete = () => {
    if (!Prm_Ord_Attachments_D) {
      newToast('WARNING', 'No tiene permisos para eliminar archivos adjuntos.', 4);
      handleCancelDelete();
      return;
    }
    
    setLoadingDelete(true);
    Delete(selectedAttachment.Oad_Id).finally(() => {
      handleCancelDelete();
      setLoadingDelete(false);
    });
  };

  const handleCancelDelete = () => {
    setModalConfirmation(false);
  };

  const handleClosePdfPopup = () => {
    setShowPdfPopup(false);
    setPdfBlobUrl(null);
  };

  if (!idOrd || idOrd === -1) {
    return <NoDataPanel title={formatMessage('OrdNoDataTitle')} description={formatMessage('OrdNoDataDescription')} />;
  }

  return (
    <div style={{ height: 'calc(100vh - 300px)', padding: '5px', overflow: 'auto', paddingBottom: isSmall || isXSmall ? `150px` : `50px` }}>
      <Toolbar>
        <Item location='before'>
          <div className='grid-header'>Administración de archivos</div>
        </Item>
        {Prm_Ord_Attachments_C && (
          <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button 
              id='OpportunityBtn' 
              icon={AddIcon} 
              text='Oportunidad' 
              stylingMode='text' 
              onClick={() => {
                if (!Prm_Ord_Attachments_C) {
                  newToast('WARNING', 'No tiene permisos para crear archivos adjuntos.', 4);
                  return;
                }
                setPopupVisible(true);
              }} 
            />
            <Tooltip target={'#OpportunityBtn'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Nuevo archivo adjunto
            </Tooltip>
          </Item>
        )}
        <Item location='before' locateInMenu='auto'>
          <div className='separator' />
        </Item>
        <Item location='after' locateInMenu='never' showText='inMenu' widget='dxButton'>
          <Button id='Showpanel' icon={panelVisible ? Pin : UnPin} text={panelVisible ? 'Panel Visible' : 'Panel oculto'} stylingMode='text' onClick={handlePanelClose} />
          <Tooltip target={'#Showpanel'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
            {panelVisible ? 'Ocultar panel' : 'Abrir panel'}
          </Tooltip>
        </Item>
      </Toolbar>

      {renderAttachedFiles()}

      <DataGrid className='grid' dataSource={data} height='auto' width={'100%'} repaintChangesOnly={false} highlightChanges={false} hoverStateEnabled={true} rowAlternationEnabled={true} showColumnLines={true}>
        <Column dataField='Id' caption='ID' width={50} allowEditing={false} visible={false} />
        <Column dataField='Codigo' caption='CÓDIGO' width={100} allowEditing={false} />
        <Column dataField='Estudio' caption='ESTUDIO' allowEditing={false} />
        <Column
          dataField='Archivo'
          caption='ARCHIVO'
          width={100}
          allowEditing={false}
          cellRender={(e) => (
            <div id={`detailFile${e.data.Id}`} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <img
                src={e.data.Archivo ? PdfIcon : UnPdfIcon}
                alt=''
                style={{ width: '20px', height: '20px', cursor: e.data.Archivo ? 'pointer' : 'default' }}
                onClick={() => {
                  if (e.data.Archivo) {
                    handleClicOpen(e.data);
                  }
                }}
              />
              <Tooltip target={`#detailFile${e.data.Id}`} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                {e.data.Archivo ? 'Ver archivo adjunto' : 'Estudio sin archivo adjunto'}
              </Tooltip>
            </div>
          )}
        />
        {Prm_Ord_Attachments_D && (
          <Column
            dataField='Eliminar'
            caption='ELIMINAR'
            width={100}
            allowEditing={false}
            cellRender={(e) => (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <i
                  onClick={() => {
                    if (e.data.Archivo) {
                      handleDeleteAttachment(e.data);
                    }
                  }}
                  className='dx-icon-trash'
                  title='Eliminar'
                  aria-label='Eliminar'
                  style={{ cursor: e.data.Archivo ? 'pointer' : 'default', opacity: e.data.Archivo ? 1 : 0.5 }}
                />
              </div>
            )}
          />
        )}
      </DataGrid>

      {isPopupVisible && (
        <Popup visible={isPopupVisible} onHiding={handleCancel} dragEnabled={true} showCloseButton onHidden={handleCancel} showTitle={true} title='Adjuntar archivo' width={400} height={'auto'}>
          <FileUploader selectButtonText='Seleccionar archivo' labelText='' accept='.pdf' uploadMode='useForm' onValueChanged={handleFileUpload} />
          <SelectBox dataSource={availableStudies} displayExpr='Es_Nombre' valueExpr='Es_Id' label='Seleccione un estudio (Opcional)' labelMode='floating' stylingMode='outlined' onValueChanged={handleStudyChange} />
          {selectedStudy && (
            <div style={{ marginTop: '10px' }}>
              <label>
                <input type='checkbox' checked={validateAsAttachment} onChange={(e) => setValidateAsAttachment(e.target.checked)} />
                Validar estudio como adjunto
              </label>
            </div>
          )}
          <ToolbarItem toolbar='bottom' location='after'>
            <Button text='Cancelar' onClick={handleCancel} stylingMode='contained' />
            <Button text={loading ? 'Cargando...' : `Cargar`} disabled={loading} onClick={handleAccept} stylingMode='contained' type='default' style={{ marginLeft: '10px' }} />
          </ToolbarItem>
        </Popup>
      )}

      <Popup visible={showPdfPopup} onHiding={handleClosePdfPopup} onHidden={handleClosePdfPopup} showCloseButton showTitle={true} title='Vista previa del archivo' fullScreen dragEnabled>
        {pdfBlobUrl ? (
          <object data={pdfBlobUrl} type='application/pdf' width='100%' height='100%'>
            <p>
              Tu navegador no soporta la visualización de PDF.{' '}
              <a href={pdfBlobUrl} target='_blank' rel='noopener noreferrer'>
                Haz clic aquí para descargarlo
              </a>
              .
            </p>
          </object>
        ) : (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>Cargando documento...</div>
        )}
      </Popup>

      <ConfirmationPopup visible={modalConfirmation} loading={loadingDelete} message='¿Está seguro de que desea eliminar este archivo adjunto?' onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} />
    </div>
  );
};

export default Index;
